import AIChatWidget from '@/components/aichat/AiChatWidget';
import ContentFeatureCard from '@/components/landing/content-feature-card';
import SeoHead from '@/components/seo/SeoHead';
import { TablePagination } from '@/components/table-components';
import LandingLayout from '@/layouts/landing-layout';
import { Link, router } from '@inertiajs/react';
import { BookOpen, ChevronRight, Compass, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const baseUrl = import.meta.env.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

const categoryOptions = ['Student Research Published', 'Faculty Publication'];

type Institute = {
    id: number;
    name: string;
};

type Research = {
    id: number;
    title: string;
    category: string;
    date: string;
    img: string | null;
    link: string;
    institute?: string | null;
    institute_id?: number | null;
    sdgs?: Array<{
        sdg_number: number;
        short_title: string;
        slug: string;
        image?: string | null;
    }>;
};

type Pagination = {
    current_page: number;
    per_page: number;
    total: number;
};

type Filters = {
    institute_id: number | null;
    category: string | null;
    search?: string | null;
};

type Props = {
    institutes: string;
    researches: string;
    pagination: string;
    filters: string;
};

export default function LandingResearch({
    institutes: encodedInstitutes,
    researches: encodedResearches,
    pagination: encodedPagination,
    filters: encodedFilters,
}: Props) {
    const [showUpdateNotice, setShowUpdateNotice] = useState(true);
    const institutes = useMemo<Institute[]>(() => {
        try {
            return JSON.parse(atob(encodedInstitutes));
        } catch {
            return [];
        }
    }, [encodedInstitutes]);

    const researches = useMemo<Research[]>(() => {
        try {
            return JSON.parse(atob(encodedResearches));
        } catch {
            return [];
        }
    }, [encodedResearches]);

    const pagination = useMemo<Pagination>(() => {
        try {
            return JSON.parse(atob(encodedPagination));
        } catch {
            return { current_page: 1, per_page: 6, total: 0 };
        }
    }, [encodedPagination]);

    const filters = useMemo<Filters>(() => {
        try {
            return JSON.parse(atob(encodedFilters));
        } catch {
            return { institute_id: null, category: null, search: '' };
        }
    }, [encodedFilters]);

    const [searchText, setSearchText] = useState(filters.search || '');

    const lastPage = pagination.per_page > 0 ? Math.max(1, Math.ceil(pagination.total / pagination.per_page)) : 1;

    const from = pagination.total === 0 ? 0 : (pagination.current_page - 1) * pagination.per_page + 1;

    const to = pagination.total === 0 ? 0 : Math.min(pagination.total, pagination.current_page * pagination.per_page);

    const handleSelectInstitute = (id: number | null) => {
        router.get(
            route('landing.research'),
            {
                institute_id: id,
                category: filters.category,
                search: searchText,
                page: 1,
            },
            {
                preserveState: true,
                preserveScroll: true,
            },
        );
    };

    const handlePageChange = (newPage: number) => {
        router.get(
            route('landing.research'),
            {
                institute_id: filters.institute_id,
                category: filters.category,
                search: searchText,
                page: newPage,
            },
            {
                preserveState: true,
                preserveScroll: true,
            },
        );
    };

    const handleSelectCategory = (category: string | null) => {
        router.get(
            route('landing.research'),
            {
                institute_id: filters.institute_id,
                category,
                search: searchText,
                page: 1,
            },
            {
                preserveState: true,
                preserveScroll: true,
            },
        );
    };

    const handleClearSearch = () => {
        setSearchText('');
        router.get(
            route('landing.research'),
            {
                institute_id: filters.institute_id,
                category: filters.category,
                search: '',
                page: 1,
            },
            {
                preserveState: true,
                preserveScroll: true,
            },
        );
    };

    useEffect(() => {
        if (searchText === (filters.search || '')) {
            return;
        }

        const timeout = window.setTimeout(() => {
            router.get(
                route('landing.research'),
                {
                    institute_id: filters.institute_id,
                    category: filters.category,
                    search: searchText,
                    page: 1,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    replace: true,
                },
            );
        }, 400);

        return () => window.clearTimeout(timeout);
    }, [filters.category, filters.institute_id, filters.search, searchText]);

    return (
        <LandingLayout>
            <SeoHead
                title="Research"
                description="Explore institutional research outputs from Northeastern College institutes."
                image={`${baseUrl}/images/logo.webp`}
            />

            <section id="research" className="bg-muted pb-16">
                 
                <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
                    <img
                        src="/images/hero/research.png"
                        alt="Northeastern College campus"
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-60"
                    />

                     
                    <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />

                     
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

                     
                    <div className="pointer-events-none absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-emerald-500/10 blur-[100px]" />
                    <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-yellow-500/5 blur-[100px]" />

                    <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                        <div className="mb-6 flex items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                            <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                                Home
                            </Link>
                            <ChevronRight className="size-4" aria-hidden="true" />
                            <span className="text-white/80">Research</span>
                        </div>

                        <h1 className="max-w-4xl text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">
                            Institutional Research Center
                        </h1>
                        <p className="mt-6 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">
                            Explore published academic papers, faculty research journals, and student publications across NC academic institutes.
                        </p>
                    </div>
                </div>

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl px-4">
                    {showUpdateNotice && (
                        <div className="bg-card border-border mb-8 flex flex-col gap-4 rounded-2xl border p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-start gap-3">
                                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-sm font-bold text-emerald-600">
                                    i
                                </span>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    This center is continuously updated. Some research publications may still be under review or pending upload.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowUpdateNotice(false)}
                                className="text-muted-foreground hover:text-foreground border-border hover:bg-muted shrink-0 rounded-full border px-4 py-2 text-xs font-semibold tracking-wider uppercase transition-all"
                            >
                                Dismiss
                            </button>
                        </div>
                    )}

                    <div className="grid gap-8 md:grid-cols-12">
                        {/* LEFT – Institutes filter */}
                        <div className="md:col-span-3">
                            <div className="bg-card border-border sticky top-24 overflow-hidden rounded-2xl border shadow-sm">
                                <div className="border-border bg-muted/30 border-b px-5 py-4">
                                    <h3 className="text-foreground flex items-center gap-2 text-xs font-bold tracking-wider uppercase">
                                        <Compass className="size-4 text-[#0A7A35]" />
                                        Filter Institutes
                                    </h3>
                                </div>

                                <div className="p-3">
                                    {/* All institutes */}
                                    <button
                                        type="button"
                                        onClick={() => handleSelectInstitute(null)}
                                        className={`mb-2 w-full rounded-full px-4 py-2.5 text-left text-sm font-semibold transition ${
                                            filters.institute_id === null
                                                ? 'bg-[#0A7A35] text-white shadow-md'
                                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                        }`}
                                    >
                                        All Institutes
                                    </button>

                                    {/* Individual institutes */}
                                    <div className="custom-scroll mt-1 max-h-[360px] space-y-1 overflow-y-auto pr-1">
                                        {institutes.map((inst) => (
                                            <button
                                                key={inst.id}
                                                type="button"
                                                onClick={() => handleSelectInstitute(inst.id)}
                                                className={`w-full rounded-xl px-4 py-2.5 text-left text-xs leading-snug font-medium transition sm:text-sm ${
                                                    filters.institute_id === inst.id
                                                        ? 'rounded-l-none border-l-4 border-[#0A7A35] bg-[#0A7A35]/10 pl-3 font-semibold text-[#0A7A35]'
                                                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                                }`}
                                            >
                                                <span className="line-clamp-2">{inst.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT – Research cards */}
                        <div className="md:col-span-9">
                            <div className="bg-card border-border mb-6 flex flex-col items-start justify-between gap-4 rounded-2xl border p-4 shadow-sm xl:flex-row xl:items-center xl:p-6">
                                <div className="flex flex-wrap gap-2 w-full xl:w-auto xl:shrink-0 xl:flex-nowrap">
                                    <button
                                        type="button"
                                        onClick={() => handleSelectCategory(null)}
                                        className={`rounded-full px-5 py-2 text-xs font-semibold transition md:text-sm ${filters.category === null ? 'bg-[#0A7A35] text-white shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                        }`}
                                    >
                                        All Categories
                                    </button>
                                    {categoryOptions.map((category) => (
                                        <button
                                            key={category}
                                            type="button"
                                            onClick={() => handleSelectCategory(category)}
                                            className={`rounded-full px-5 py-2 text-xs font-semibold transition md:text-sm ${filters.category === category ? 'bg-[#0A7A35] text-white shadow-sm' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                            }`}
                                        >
                                            {category === 'Student Research Published' ? 'Student Research' : 'Faculty Research'}
                                        </button>
                                    ))}
                                </div>

                                {/* Search Input */}
                                <div className="flex w-full items-center gap-2 xl:max-w-md">
                                    <div className="relative w-full">
                                        <Search className="text-muted-foreground absolute top-1/2 left-4 size-4 -translate-y-1/2" />
                                        <input
                                            type="text"
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            placeholder="Search research titles or keywords..."
                                            className="border-border bg-card text-foreground w-full rounded-full border py-2.5 pr-10 pl-11 text-sm shadow-inner transition-all duration-200 focus:border-[#0A7A35] focus:ring-2 focus:ring-[#0A7A35]/30 focus:outline-none"
                                        />
                                        {searchText && (
                                            <button
                                                type="button"
                                                onClick={handleClearSearch}
                                                className="hover:bg-muted text-muted-foreground absolute top-1/2 right-4 -translate-y-1/2 rounded-full p-0.5 transition-colors"
                                            >
                                                <X className="size-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {researches.length === 0 ? (
                                <div className="bg-card border-border flex flex-col items-center justify-center rounded-2xl border px-4 py-20 text-center shadow-sm">
                                    <div className="bg-muted text-muted-foreground mb-4 rounded-full p-4">
                                        <BookOpen className="size-10" />
                                    </div>
                                    <h3 className="text-foreground mb-1 text-lg font-bold">No publications found</h3>
                                    <p className="text-muted-foreground max-w-md text-sm">
                                        We couldn't find any publications matching your selected filters. Try choosing a different institute or
                                        category.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                        {researches.map((r) => (
                                            <ContentFeatureCard
                                                key={r.id}
                                                title={r.title}
                                                image={r.img}
                                                institute={r.institute}
                                                badges={r.category ? [r.category === 'Student Research Published' ? 'Student Research' : 'Faculty Research'] : []}
                                                date={r.date}
                                                linkHref={r.link}
                                                sdgs={r.sdgs}
                                            />
                                        ))}
                                    </div>

                                    {/* Pagination */}
                                    <div className="mt-8">
                                        <TablePagination
                                            hidePerPage
                                            currentPage={pagination.current_page}
                                            lastPage={lastPage}
                                            perPage={pagination.per_page}
                                            total={pagination.total}
                                            from={from}
                                            to={to}
                                            onPageChange={handlePageChange}
                                            onPerPageChange={() => {}}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Custom scrollbar */}
                <style>{`
                    .custom-scroll::-webkit-scrollbar {
                        width: 5px;
                    }
                    .custom-scroll::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    .custom-scroll::-webkit-scrollbar-thumb {
                        background: rgba(10, 122, 53, 0.2);
                        border-radius: 999px;
                    }
                    .custom-scroll::-webkit-scrollbar-thumb:hover {
                        background: rgba(10, 122, 53, 0.4);
                    }
                `}</style>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
